import React, { useMemo, useState } from 'react';

import { startCase } from 'lodash';
import { func, shape, string } from 'prop-types';
import { PROJECT_TYPES_ENUMS } from '../../../../core/constants/projects';
import ApprovalAnalytics from '../approval-analytics';
import ApprovalModeratedVideoLink from '../approval-moderated-video-link';
import ApprovalNoTestersFound from '../approval-no-testers-found';
import ApprovalParticipant from '../approval-participant';
import ApprovalUnmoderatedVideo from '../approval-unmoderated-video';
import ApprovalDidNotShow from '../approval-did-not-show';
import ApprovalSurvey from '../approval-survey/index';
import ApprovalTreeTestSurvey from '../approval-tree-test-survey/index';
import Header from './header';

import {
  CardRow,
  Segment,
} from './styles';
import { APPROVAL_TYPE } from './constants';

const Approval = (props) => {
  const {
    approvalType,
    project,
    onSetCurrentTester,
    refetch,
  } = props;
  const [expanded, setExpanded] = useState(false);

  const renderApproval = () => {
    const approvalComponents = {
      [APPROVAL_TYPE.PARTICIPANT]: (
        <ApprovalParticipant
          project={project}
          onSetCurrentTester={onSetCurrentTester}
          refetch={refetch}
        />
      ),
      [APPROVAL_TYPE.NO_TESTERS_FOUND]: (
        <ApprovalNoTestersFound project={project} refetch={refetch} />
      ),
      [APPROVAL_TYPE.UNMODERATED_VIDEO]: (
        <ApprovalUnmoderatedVideo project={project} refetchParticipants={refetch} />
      ),
      [APPROVAL_TYPE.MODERATED_VIDEO_LINK]: (
        <ApprovalModeratedVideoLink project={project} refetch={refetch} />
      ),
      [APPROVAL_TYPE.ANALYTICS]: (
        <ApprovalAnalytics project={project} refetch={refetch} />
      ),
      [APPROVAL_TYPE.DID_NOT_SHOW]: (
        <ApprovalDidNotShow project={project} />
      ),
      [APPROVAL_TYPE.CARD_SORT_PARTICIPANT]: (
        <ApprovalParticipant
          project={project}
          onSetCurrentTester={onSetCurrentTester}
          refetch={refetch}
        />
      ),
      [APPROVAL_TYPE.CARD_SORT_SURVEY]: (
        <ApprovalSurvey project={project} refetch={refetch} />
      ),
      [APPROVAL_TYPE.TREE_TEST_PARTICIPANT]: (
        <ApprovalParticipant
          project={project}
          onSetCurrentTester={onSetCurrentTester}
          refetch={refetch}
        />
      ),
      [APPROVAL_TYPE.TREE_TEST_SURVEY]: (
        <ApprovalTreeTestSurvey project={project} refetch={refetch} />
      ),
      [APPROVAL_TYPE.SURVEY_PARTICIPANT]: (
        <ApprovalParticipant
          project={project}
          onSetCurrentTester={onSetCurrentTester}
          refetch={refetch}
        />
      ),
      [APPROVAL_TYPE.SURVEY_RESPONSE]: (
        <ApprovalSurvey project={project} refetch={refetch} />
      ),
    };

    return approvalComponents[approvalType] || null;
  };

  const onExpandButtonClicked = () => {
    setExpanded((prevExpanded) => !prevExpanded);
  };

  const approvalPrefix = project.testType === PROJECT_TYPES_ENUMS.MODERATED && approvalType === 'participant' ? 'Moderated' : '';

  const headerContent = {
    approvalType: `${approvalPrefix} ${startCase(approvalType)}`,
    company: project.company.name,
    projectName: project.name,
    createdOn: project.createdAt,
    launchedOn: project.launchedOn,
  };

  const isProjectDraftUserInterviews = useMemo(() => project.segments.every((segment) => segment.userInterviewsProjectCreatedAt
      && segment.userInterviewsProjectId
      && segment.userInterviewsProjectLaunch === false
      && segment.userInterviewsProjectLaunchAt === null
      && Array.isArray(segment.recruitParticipantTokens)
      && segment.recruitParticipantTokens.every((token) => token.deletedAt !== null)), [project.segments]);

  const isParticipantCountOverLinkLimit = useMemo(() => project.segments.some((segment) => segment.recruitParticipantTokens
    .some((link) => (!link.deletedAt && link.maximumTests <= segment.audienceParticipants))), [project.segments]);

  return (
    <CardRow>
      <Segment showOutline={isParticipantCountOverLinkLimit || isProjectDraftUserInterviews}>
        <Header
          content={headerContent}
          expanded={expanded}
          onExpandButtonClicked={onExpandButtonClicked}
          approvalType={approvalType}
          projectId={project.id}
          participantsSource={project.participantsSource}
          refetch={refetch}
        />
        {expanded && renderApproval()}
      </Segment>
    </CardRow>
  );
};

Approval.propTypes = {
  approvalType: string.isRequired,
  project: shape({
    name: string,
    testType: string,
    id: string,
    createdAt: string,
    launchedOn: string,
    participantsSource: string,
    company: shape({
      name: string,
    }),
  }).isRequired,
  refetch: func.isRequired,
  onSetCurrentTester: func.isRequired,
};

export default Approval;
